import { $, $$ } from "@utils/dom";
import { on, off } from "@utils/listener";
import { lerp2 } from "@utils/math";
import { motion_reduced } from "@utils/mobile";
import RAF from "@utils/raf";

var Z_INDEX = 10;

class PbRowTestimonials {
  constructor(el, emitter) {
    this.el = el;
    this.emitter = emitter;
    this.testimonials = [ ...$$('.pb-row-testimonials__testimonial', this.el) ].map(testimonial => new Testimonial(testimonial));
    this.navItems = [ ...$$('.pb-row-testimonials__navItem', this.el) ];
    this.pagination = $('.pb-row-testimonials__currentPage', this.el);
    this.prevBtn = $('.pb-row-testimonials__prevBtn', this.el);
    this.nextBtn = $('.pb-row-testimonials__nextBtn', this.el);

    this._index = 0;
    this._length = this.testimonials.length;

    this._onNavItem = this._onNavItem.bind(this);
    this._onPrev = this._onPrev.bind(this);
    this._onNext = this._onNext.bind(this);
  }

  init() {
    this._bindEvents();

  }
  destroy() {
    this._unbindEvents();

    if( this.testimonials ) this.testimonials.forEach(testimonial => testimonial.destroy());

    this.el = null;
    this.emitter = null;
    this.testimonials = null;
    this.navItems = null;
    this.pagination = null;
    this.prevBtn = null;
    this.nextBtn = null;

    this._index = null;
    this._length = null;

    this._onNavItem = null;
    this._onPrev = null;
    this._onNext = null;
  }

  goTo( index ) {
    // apply limits
    if( index < 0 ) index = 0;
    else if( index >= this._length ) index = this._length - 1;

    // stop early if index hasn't changed
    if( index === this._index ) return;

    // update current index
    this._index = index;

    this.navItems.forEach((navItem, i) => navItem.setAttribute('aria-selected', i === this._index));
    this.testimonials.forEach((testimonial, i) => testimonial[i === this._index ? 'show' : 'hide']());

    // update UI
    if( this.prevBtn ) {
      if( this._index === 0 ) this.prevBtn.setAttribute('disabled', '');
      else this.prevBtn.removeAttribute('disabled');
    }
    if( this.nextBtn ) {
      if( this._index >= this._length - 1 ) this.nextBtn.setAttribute('disabled', '');
      else this.nextBtn.removeAttribute('disabled');
    }
    if( this.pagination ) this.pagination.textContent = this._index + 1;
  }
  previous() { this.goTo(this._index - 1); }
  next() { this.goTo(this._index + 1); }

  _bindEvents() {
    if( this.navItems ) on(this.navItems, 'click', this._onNavItem);
    if( this.prevBtn ) on(this.prevBtn, 'click', this._onPrev);
    if( this.nextBtn ) on(this.nextBtn, 'click', this._onNext);
  }
  _unbindEvents() {
    if( this.navItems ) off(this.navItems, 'click', this._onNavItem);
    if( this.prevBtn ) off(this.prevBtn, 'click', this._onPrev);
    if( this.nextBtn ) off(this.nextBtn, 'click', this._onNext);
  }

  _onNavItem(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    const index = this.navItems.findIndex(item => item === event.currentTarget);
    if( index < 0 ) return;

    this.goTo(index);
  }
  _onPrev(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.previous();
  }
  _onNext(event) {
    if( event ) {
      event.preventDefault();
      event.stopImmediatePropagation();
    }

    this.next();
  }
}

class Testimonial {
  constructor(el) {
    this.el = el;
    this.media = $('.pb-row-testimonials__testimonial__media', this.el);
    this.img = $('.pb-row-testimonials__testimonial__img', this.el);
    this.icon = $('.pb-row-testimonials__testimonial__icon path', this.el);

    this._visible = this.el.hasAttribute('inert') ? false : true;
    this._raf = null;
    this._state = {
      media: { current: 0, target: 0 },
      imgY: { current: 0, target: 0 },
      imgScale: { current: 1, target: 1 },
      icon: { current: 0, target: 0 },
    };

    this._onRAF = this._onRAF.bind(this);
    this._onHideCompleted = this._onHideCompleted.bind(this);
  }

  destroy() {
    if( this._raf ) this._raf(false);
    RAF.remove(this._onRAF);

    this.el = null;
    this.media = null;
    this.img = null;
    this.icon = null;

    this._visible = null;
    this._raf = null;
    this._state = null;

    this._onRAF = null;
    this._onHideCompleted = null;

    Z_INDEX = 0;
  }

  show() {
    if( this._visible ) return;
    this._visible = true;

    if( motion_reduced ) {
      this._state.media.current = this._state.media.target = 0;
      this._state.imgY.current = this._state.imgY.target = 0;
      this._state.imgScale.current = this._state.imgScale.target = 1;
      this._state.icon.current = this._state.icon.target = 0;
    } else {
      this._state.media.current = 100;
      this._state.media.target = 0;
      this._state.imgY.current = -20;
      this._state.imgY.target = 0;
      this._state.imgScale.current = 1.2;
      this._state.imgScale.target = 1;
      this._state.icon.current = 100;
      this._state.icon.target = 0;
    }
    

    this.el.style.zIndex = Z_INDEX++;
    this.el.setAttribute('aria-hidden', false);
    this.el.removeAttribute('inert');

    this._render();
    this._startRAF();
  }
  hide() {
    if( !this._visible ) return;
    this._visible = false;

    this.el.setAttribute('aria-hidden', true);

    this._state.imgY.target = 20;
    this._state.icon.target = -100;

    if( motion_reduced ) {
      this._state.imgY.current = this._state.imgY.target;
      this._state.icon.current = this._state.icon.target;
    }

    this._startRAF();
  }

  _startRAF() {
    if( !this._raf ) this._raf = RAF.add(this._onRAF);
    this._raf(true);
  }
  _render() {
    if( this.media ) this.media.style.clipPath = `inset(0 0 ${this._state.media.current}% 0 round var(--radius))`;
    if( this.img ) {
      this.img.style.translate = `0 ${this._state.imgY.current}%`;
      this.img.style.scale = `${this._state.imgScale.current}`;
    }
    if( this.icon ) this.icon.style.transform = `translateX(${this._state.icon.current}%)`;
  }

  _onRAF(delta) {
    this._state.media.current = lerp2(this._state.media.current, this._state.media.target, 0.08, delta);
    this._state.imgY.current = lerp2(this._state.imgY.current, this._state.imgY.target, 0.1, delta);
    this._state.imgScale.current = lerp2(this._state.imgScale.current, this._state.imgScale.target, 0.06, delta);
    this._state.icon.current = lerp2(this._state.icon.current, this._state.icon.target, 0.1, delta);

    this._render();

    const mediaEnd = Math.abs(this._state.media.target - this._state.media.current) < 0.1;
    const imgYEnd = Math.abs(this._state.imgY.target - this._state.imgY.current) < 0.05;
    const imgScaleEnd = Math.abs(this._state.imgScale.target - this._state.imgScale.current) < 0.01;
    const iconEnd = Math.abs(this._state.imgScale.target - this._state.imgScale.current) < 0.1;

    if( mediaEnd && imgYEnd && imgScaleEnd && iconEnd ) {
      this._raf(false);
      if( !this.visible ) this._onHideCompleted();
    }
  }
  _onHideCompleted() {
    this.el.setAttribute('inert', '');
  }

  // getter - setter
  get visible() { return this._visible; }
}

export default PbRowTestimonials;
